import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ClickerHeroSection from '@/components/ClickerHeroSection';
import TestimonialSection from '@/components/TestimonialSection';
import PersonaToggle from '@/components/PersonaToggle';
import AccountabilitySection from '@/components/AccountabilitySection';
import FeatureShowcase from '@/components/FeatureShowcase';
import AllInOnePlatform from '@/components/AllInOnePlatform';
import OutputGallery from '@/components/OutputGallery';
import MoatSection from '@/components/MoatSection';
import CompetitorComparison from '@/components/CompetitorComparison';
import PricingSection from '@/components/PricingSection';
import UrgencySection from '@/components/UrgencySection';
import SecurityFooter from '@/components/SecurityFooter';
import OptimizedOnboardingV2 from '@/components/OptimizedOnboardingV2';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Check, Star, Crown, MousePointer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ClickerLanding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [clickCounter, setClickCounter] = useState(0);
  const navigate = useNavigate();

  // Handler for sign in clicks
  const handleSignInClick = () => {
    navigate('/login');
  };

  // Optimized pricing section
  const PricingSection = () => (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-800">
            Simple Pricing
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Start with a 3-day free trial. No setup fees, no contracts. Cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Growth Plan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-blue-50 border-2 border-blue-500 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 relative"
          >
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-blue-600 text-white">
                <Crown className="w-3 h-3 mr-1" />
                Most Popular
              </Badge>
            </div>
            
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Growth</h3>
              <div className="text-4xl font-bold text-blue-600 mb-1">$97</div>
              <div className="text-sm text-gray-600 mb-2">$0 today → $97 after 3-day trial</div>
              <p className="text-gray-600 mb-6">For scaling businesses</p>
              
              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">150,000 emails/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">AI-powered campaigns</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Automated lead sourcing</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Meeting booking</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Email automation</span>
                </li>
              </ul>
              
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => setShowOnboarding(true)}
              >
                Start 3-Day Trial
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Pro</h3>
              <div className="text-4xl font-bold text-gray-900 mb-4">$297</div>
              <p className="text-gray-600 mb-6">For high-volume senders</p>
              
              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">500,000 emails/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Everything in Growth</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Advanced AI optimization</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">White-label options</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Dedicated support</span>
                </li>
              </ul>
              
              <Button 
                variant="outline" 
                className="w-full border-gray-300"
                onClick={() => setShowOnboarding(true)}
              >
                Start Pro Trial
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Guarantee */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-50 border border-green-200 rounded-full">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">
              30-day money-back guarantee • Cancel anytime • No setup fees
            </span>
          </div>
        </div>
      </div>
    </section>
  );

  // Click Counter Section
  const ClickCounterSection = () => {
    // Increment click counter
    const handleClick = () => {
      setClickCounter(prev => prev + 1);
    };

    return (
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-red-900/30 text-red-300 border-red-800">
              <MousePointer className="w-3 h-3 mr-1" />
              Click Counter
            </Badge>
            <h2 className="text-4xl font-bold text-white mb-6">
              Break Free From Endless Clicking
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Every click represents time you could spend building instead
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-slate-800 rounded-xl p-8 shadow-lg border border-slate-700"
          >
            <div className="space-y-8">
              <div className="text-center">
                <div 
                  className="w-32 h-32 mx-auto bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center cursor-pointer hover:scale-105 transition-transform duration-300 shadow-lg shadow-red-500/20"
                  onClick={handleClick}
                >
                  <div className="text-4xl font-bold text-white">{clickCounter}</div>
                </div>
                <p className="mt-4 text-gray-400">Click the circle to simulate your daily grind</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-700 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold mb-4 text-white">Manual Outreach</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm text-gray-300 mb-1">
                        <span>Clicks per prospect</span>
                        <span>~25</span>
                      </div>
                      <div className="w-full bg-slate-600 h-2 rounded-full">
                        <div className="bg-red-500 h-2 rounded-full" style={{ width: '80%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm text-gray-300 mb-1">
                        <span>Clicks per day</span>
                        <span>~500</span>
                      </div>
                      <div className="w-full bg-slate-600 h-2 rounded-full">
                        <div className="bg-red-500 h-2 rounded-full" style={{ width: '90%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm text-gray-300 mb-1">
                        <span>Clicks per month</span>
                        <span>~10,000</span>
                      </div>
                      <div className="w-full bg-slate-600 h-2 rounded-full">
                        <div className="bg-red-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-700 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold mb-4 text-white">With Munger.ai</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm text-gray-300 mb-1">
                        <span>Clicks per prospect</span>
                        <span>0</span>
                      </div>
                      <div className="w-full bg-slate-600 h-2 rounded-full">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '0%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm text-gray-300 mb-1">
                        <span>Clicks per day</span>
                        <span>~5</span>
                      </div>
                      <div className="w-full bg-slate-600 h-2 rounded-full">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '5%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm text-gray-300 mb-1">
                        <span>Clicks per month</span>
                        <span>~100</span>
                      </div>
                      <div className="w-full bg-slate-600 h-2 rounded-full">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '10%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-900/50 to-indigo-900/50 p-6 rounded-xl border border-blue-800/50">
                <div className="flex flex-col md:flex-row justify-between items-center">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">Clicks Eliminated</h4>
                    <p className="text-gray-400">Monthly clicks you'll never have to make:</p>
                  </div>
                  <div className="text-4xl font-bold text-blue-400">9,900+</div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-400 mb-1">
                    <span>Efficiency Gain</span>
                    <span>99%</span>
                  </div>
                  <div className="w-full bg-slate-600 h-3 rounded-full">
                    <div className="bg-blue-500 h-3 rounded-full" style={{ width: '99%' }}></div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 px-8 py-4 text-lg font-semibold"
                  onClick={() => setShowOnboarding(true)}
                >
                  Break Free Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  // Final CTA Section
  const FinalCTASection = () => (
    <section className="py-20 bg-blue-600">
      <div className="max-w-4xl mx-auto px-6 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Stop Being a Clicking Machine?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join 2,100+ builders who let AI handle the clicking. Setup takes 10 minutes.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Button 
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              onClick={() => setShowOnboarding(true)}
            >
              Break Free Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            
            <Button 
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-semibold"
              onClick={() => navigate('/book-demo')}
            >
              Book Demo
            </Button>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-blue-100">
              Already have an account?{' '}
              <Button 
                variant="link"
                className="p-0 h-auto text-sm underline hover:text-white transition-colors"
                onClick={handleSignInClick}
              >
                Sign in
              </Button>
            </p>
          </div>
          
          <div className="flex items-center justify-center gap-6 text-sm text-blue-100">
            <span>✓ 10-minute setup</span>
            <span>✓ First replies in 24 hours</span>
            
          </div>
        </motion.div>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <ClickerHeroSection onStartClick={() => setShowOnboarding(true)} />
      
      {/* Click Counter Section - Key feature for this landing page */}
      <ClickCounterSection />
      
      {/* Testimonials */}
      <TestimonialSection />
      
      {/* Urgency Section */}
      <UrgencySection onStartClick={() => setShowOnboarding(true)} />
      
      {/* Persona Toggle */}
      <PersonaToggle />
      
      {/* Accountability Section */}
      <AccountabilitySection />
      
      {/* How It Works */}
      <FeatureShowcase />
      
      {/* ROI Gallery */}
      <OutputGallery />
      
      {/* All-in-One Platform */}
      <AllInOnePlatform onStartClick={() => setShowOnboarding(true)} />
      
      {/* Competitor Comparison */}
      <CompetitorComparison onStartClick={() => setShowOnboarding(true)} />
      
      {/* Trust/Authority Section */}
      <MoatSection />
      
      {/* Pricing */}
      <PricingSection />
      
      {/* Final CTA */}
      <FinalCTASection />
      
      {/* Security Footer */}
      <SecurityFooter />
      
      {/* Optimized Onboarding Modal */}
      <OptimizedOnboardingV2 
        isOpen={showOnboarding} 
        onClose={() => setShowOnboarding(false)} 
      />
    </div>
  );
};

export default ClickerLanding; 